import currency from "currency.js";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import {
  AccountTypeResponse,
  GetOrders200Response,
  InstrumentTypeResponse,
  SideResponse,
} from "../../../../codegen-api";
import { CancelAllOrdersModal } from "../../../../components/ConfirmationModal/CancelAllOrdersModal";
import EditOrderModal from "../../../../components/ConfirmationModal/EditOrderModal";
import RemoveOrderModal from "../../../../components/ConfirmationModal/RemoveOrderModal";
import { PortfolioMarketFilter } from "../../../../components/PortfolioSettings/PortfolioMarketFilter";
import { useUrlInstrumentFilter } from "../../../../hooks/useUrlParamState";
import MarketCell from "../../../../components/PortfolioSettings/Table/MarketCell";
import { Content } from "../../../../components/PortfolioSettings/style";
import RowActionButton from "../../../../components/shared/RowActionButton";
import { Spinner } from "../../../../components/shared/Spinner";
import { SpinnerContainerWrapper } from "../../../../components/shared/Spinner/style";
import OrderDetailsModal from "../../../../components/shared/ViewDetailsModal/OrderDetailsModal";
import { TEXT_COLORS } from "../../../../constants/design/colors";
import { SPACING } from "../../../../constants/design/spacing";
import { useGetAccount } from "../../../../hooks/api/account/useGetAccount";
import { useOrder } from "../../../../hooks/api/order/useOrder";
import { useInstrumentResolver } from "../../../../hooks/markets/useInstrumentResolver";
import { floorSizeForDisplay } from "../../../../utils/format";
import { getStopOrderName } from "../../../../utils/order";
import { CancelButton } from "../../../OrdersTab/style";
import { Label } from "../../../PortfolioOverviewTab/style";
import {
  Actions,
  Footer,
  Header,
  MobilePortfolioRow,
  MobileWrapper,
  Pill,
  PortfolioFilterContainer,
  Stat,
  StatValue,
  Stats,
} from "../style";
import { FeatureFlagKey, isFeatureEnabled } from "../../../../config/features";

interface IMobileOrdersProps {
  orders?: GetOrders200Response[];
  t: TFunction;
}

function MobileOrders({ orders, t }: IMobileOrdersProps) {
  const { t: translateAll } = useTranslation();
  const { resolveInstrumentName, resolveMarketPrecision } =
    useInstrumentResolver();

  const [removingOrder, setRemovingOrder] = useState<GetOrders200Response>();
  const [showRemoveOrderModal, setShowRemoveOrderModal] = useState(false);

  const [editingOrder, setEditingOrder] = useState<GetOrders200Response>();
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);

  const [viewDetailsForOrder, setViewDetailsForOrder] =
    useState<GetOrders200Response>();

  const onCancelOrder = useCallback((order?: GetOrders200Response) => {
    if (!order) {
      return;
    }
    setRemovingOrder(order);
    setViewDetailsForOrder(undefined);
    setShowRemoveOrderModal(true);
  }, []);

  const onEditOrder = useCallback((order?: GetOrders200Response) => {
    if (!order) {
      return;
    }
    setEditingOrder(order);
    setViewDetailsForOrder(undefined);
    setShowEditOrderModal(true);
  }, []);

  return (
    <>
      <RemoveOrderModal
        order={removingOrder}
        onHide={() => setShowRemoveOrderModal(false)}
        show={showRemoveOrderModal}
      />
      <EditOrderModal
        order={editingOrder}
        onHide={() => setShowEditOrderModal(false)}
        show={showEditOrderModal}
      />
      <OrderDetailsModal
        order={viewDetailsForOrder}
        onHide={() => setViewDetailsForOrder(undefined)}
        onEditOrder={() => onEditOrder(viewDetailsForOrder)}
        onRemoveOrder={() => onCancelOrder(viewDetailsForOrder)}
      />
      <div>
        {orders?.map((order, i) => {
          const resolvedOrder = {
            ...order,
            instrument_name: resolveInstrumentName(order) || order.instrument_name,
          };
          const { price_precision, amount_precision, amount_step } =
            resolveMarketPrecision(order);
          // Size and fill are floored at the step's precision (trimmed to the
          // market's display precision): `toFixed(2)` showed a 0.0049 BTC fill
          // as "0.00".
          return (
            <MobilePortfolioRow
              key={order.order_id}
              transition={{
                delay: orders.length > 10 ? 0 : i * 0.1,
                duration: 0.5,
                ease: "easeInOut",
              }}
              initial={{
                transform: "translateY(-8px)",
                opacity: 0,
              }}
              animate={{
                transform: "translateY(0px)",
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
            >
              <Header>
                <MarketCell
                  instrumentName={resolvedOrder.instrument_name}
                  optionType={resolvedOrder.option_type}
                  expiry={resolvedOrder.expiry}
                  strike={resolvedOrder.strike}
                />
                <Pill $isPositive={resolvedOrder.side === SideResponse.Buy}>
                  {resolvedOrder.side === SideResponse.Buy
                    ? t("bid")
                    : t("offer")}
                </Pill>
              </Header>
              <Footer>
                <Stats>
                  <Stat>
                    <Label>{t("price")}</Label>
                    <StatValue>
                      {currency(resolvedOrder.price, {
                        precision: price_precision,
                      }).format()}
                    </StatValue>
                  </Stat>
                  <Stat>
                    <Label>{t("size_fill")}</Label>
                    <StatValue>
                      <span style={{ color: TEXT_COLORS.one }}>
                        {floorSizeForDisplay(resolvedOrder.amount, amount_step, amount_precision)}
                      </span>
                      <span>&nbsp;&nbsp;/&nbsp;&nbsp;</span>
                      {floorSizeForDisplay(resolvedOrder.filled, amount_step, amount_precision)}
                    </StatValue>
                  </Stat>
                </Stats>
                <Stats>
                  <Stat>
                    <Label>{t("order_type")}</Label>
                    <StatValue>
                      {resolvedOrder.stop
                        ? getStopOrderName(
                            translateAll,
                            resolvedOrder.order_type,
                            resolvedOrder.stop,
                            true
                          )
                        : resolvedOrder.order_type}
                    </StatValue>
                  </Stat>
                  <Stat>
                    <Label>{t("margin")}</Label>
                    <StatValue>
                      {currency(resolvedOrder.initial_margin || 0, {
                        precision: price_precision,
                      }).format()}
                    </StatValue>
                  </Stat>
                </Stats>
              </Footer>
              <Actions>
                <RowActionButton
	                  variant={"close"}
	                  onClick={() => onCancelOrder(resolvedOrder)}
                  style={{ marginRight: `${SPACING.two}px` }}
                />
                {!resolvedOrder.close_position &&
                  isFeatureEnabled(FeatureFlagKey.EDIT_ORDER) && (
	                  <RowActionButton
	                    variant={"edit"}
	                    onClick={() => onEditOrder(resolvedOrder)}
                    style={{ marginRight: `${SPACING.two}px` }}
                  />
                )}
	                <RowActionButton
	                  variant={"details"}
	                  onClick={() => setViewDetailsForOrder(resolvedOrder)}
                />
              </Actions>
            </MobilePortfolioRow>
          );
        })}
      </div>
    </>
  );
}
interface IMobilePositionsTabProps {
  showFilter?: boolean;
  noPadding?: boolean;
}

export function MobileOrdersTab({
  showFilter = true,
  noPadding = false,
}: IMobilePositionsTabProps) {
  const { data: ordersData, isValidating: ordersDataValidating } = useOrder();
  const ordersLoading = !ordersData && ordersDataValidating;
  const [filter, setFilter] = useUrlInstrumentFilter();
  const { data: accountData } = useGetAccount();
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const { t } = useTranslation("app", {
    keyPrefix: "pages.PortfolioPage.mobile.MobileOrdersTab",
  });

  const canCancelAllOrders = useMemo(
    () =>
      accountData?.account_type === AccountTypeResponse.MarketMaker &&
      (ordersData || []).length > 0,
    [accountData?.account_type, ordersData]
  );

  const filteredOrders = useMemo(() => {
    if (filter === InstrumentTypeResponse.Option) {
      return ordersData?.filter((p) => !!p.option_type);
    }
    if (filter === InstrumentTypeResponse.Perpetual) {
      return ordersData?.filter(
        (p) => p.instrument_type === InstrumentTypeResponse.Perpetual
      );
    }
    if (filter === InstrumentTypeResponse.Spot) {
      return ordersData?.filter(
        (p) => p.instrument_type === InstrumentTypeResponse.Spot
      );
    }
    return ordersData;
  }, [filter, ordersData]);

  if (ordersLoading) {
    return (
      <SpinnerContainerWrapper>
        <Spinner />
      </SpinnerContainerWrapper>
    );
  }

  return (
    <MobileWrapper $noPadding={noPadding}>
      {showFilter ? (
        <PortfolioFilterContainer style={{ display: "flex" }}>
          <PortfolioMarketFilter
            title={t("orders")}
            instrumentType={filter}
            onSetFilter={setFilter}
          />
          {!canCancelAllOrders && (
            <CancelButton
              type="button"
              onClick={() => setShowCancelModal(true)}
            >
              {t("cancel_all_orders")}
            </CancelButton>
          )}
        </PortfolioFilterContainer>
      ) : null}
      <CancelAllOrdersModal
        instrumentType={filter}
        orders={filteredOrders}
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
      />
      {!filteredOrders || filteredOrders?.length === 0 ? (
        <Content>{t("no_history_desc")}</Content>
      ) : (
        <MobileOrders orders={filteredOrders} t={t} />
      )}
    </MobileWrapper>
  );
}
